import React, { PureComponent } from 'react';
import T from 'prop-types';
import { connect } from 'react-redux';
import { Episode, MediaFile } from 'client/resources';
import updateEntity from 'app/entity/actions/update';
import resourceProp from 'shared/helpers/resource-prop';
import replaceEpisodeAudioFile from 'shared/helpers/replace-episode-audio-file';
import { Loader } from '@acast/decibel';
import uploader from 'services/file-upload';
import Encoder, { JobStatus } from 'services/encoder';
import ControlBar from './control-bar';
import MarkersList from './markers-list';
import MarkerSettings from './marker-settings';
import NoMarkerSelected from './marker-settings/components/no-marker-selected';
import AudioContainer from './audio-container';
import PreviewPlayer from './preview-player';
import Timeline from './timeline';
import {
  MARKER_TYPES,
  getAudioMarker,
  getAudioMarkerUrl,
  validateMarker,
} from '../../../shared/helpers/markers';
import { markerPropType } from '../../../customPropTypes';
import { createMarker, deleteMarker, updateMarker, selectMarkers } from '../../../ducks/markers';

const EMPTY_AUDIO_SOURCE = '...';
const MIN_WAVEFORM_ZOOM_LEVEL = 1;
const MAX_WAVEFORM_ZOOM_LEVEL = 5;

class EditorView extends PureComponent {
  static propTypes = {
    episode: resourceProp(Episode),
    markers: T.arrayOf(markerPropType),
    mediaFile: resourceProp(MediaFile),
    audioSource: T.string,
    isPlayingAudio: T.bool.isRequired,
    audioProgress: T.number.isRequired,
    playAudio: T.func.isRequired,
    pauseAudio: T.func.isRequired,
    seekAudio: T.func.isRequired,
    isPlayingAdJingle: T.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedMarkerId: undefined,
      waveformZoomLevel: 1,
    };
  }

  componentDidMount() {
    this.maybeSelectMidrollMarker(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.maybeSelectMidrollMarker(nextProps);
  }

  maybeSelectMidrollMarker({ markers, mediaFile }) {
    const hasMarkers = markers && markers.length > 0;
    const hasMediaFile = Boolean(mediaFile.data);
    const hasSelectedMarker = Boolean(this.state.selectedMarkerId);

    if (hasMarkers && hasMediaFile && !hasSelectedMarker) {
      const mediaDuration = mediaFile.data.duration;
      const midrollMarker = markers.find(m => m.body && m.body.placement === 'midroll');

      if (midrollMarker && validateMarker(midrollMarker, mediaDuration)) {
        this.setState({ selectedMarkerId: midrollMarker.id });
      }
    }
  }

  getUploadingOverlayDetails() {
    const { markers } = this.props;
    const audioMarker = getAudioMarker(markers);

    if (audioMarker) {
      const upload = uploader.get(audioMarker.id, 'media');
      if (upload) {
        const uploadPercent = Math.floor(upload.progress * 100);
        return {
          info: `Uploading audio... (${uploadPercent})`,
          showSpinner: true,
        };
      }
    }

    return null;
  }

  getEncodingOverlayDetails() {
    const mediaUrl = getAudioMarkerUrl(this.props.markers);

    if (mediaUrl) {
      const encoding = Encoder.getJobForMediaUrl(mediaUrl);

      if (!encoding) {
        return null;
      }
      const { status } = encoding;

      if (status === JobStatus.Failed) {
        return {
          info: 'Encoding failed.',
          showSpinner: false,
        };
      }

      if (status !== JobStatus.Completed && status !== JobStatus.CompletedWithoutUpdate) {
        return {
          info: 'Encoding audio...',
          showSpinner: true,
        };
      }
    }

    return null;
  }

  getOverlayDetails() {
    return this.getUploadingOverlayDetails() || this.getEncodingOverlayDetails();
  }

  maybeRenderOverlay() {
    const overlayDetails = this.getOverlayDetails();

    if (overlayDetails) {
      return this.renderOverlay(overlayDetails);
    }
    return null;
  }

  handleAudioProgress = audioProgress => {
    this.setState({ audioProgress });
  };

  handleSelectMarker = selectedMarkerId => {
    this.setState({ selectedMarkerId });
  };

  handleDeleteMarker = marker => {
    this.props.onDeleteMarker(marker);
    this.setState({ selectedMarkerId: undefined });
  };

  handleAddMarker = () => {
    const { audioProgress, episode, onCreateMarker } = this.props;

    onCreateMarker({ episodeId: episode.data.id, start: audioProgress }).then(marker => {
      this.setState({ selectedMarkerId: marker.id });
    });
  };

  handleZoom = step => {
    const prevZoomLevel = this.state.waveformZoomLevel;
    const nextZoomLevel = Math.max(
      MIN_WAVEFORM_ZOOM_LEVEL,
      Math.min(MAX_WAVEFORM_ZOOM_LEVEL, prevZoomLevel + step)
    );
    this.setState({ waveformZoomLevel: nextZoomLevel });
  };

  handleAudioFileUpload = event => {
    const [file] = event.target.files || [];

    if (file) {
      const { episode, markers } = this.props;
      replaceEpisodeAudioFile(episode.data.id, episode.data.channel, file, markers);
    }
  };

  handleUpdateMarker = (episodeId, markerId, start) => {
    const { episode, onUpdateMarker } = this.props;
    onUpdateMarker(episodeId, markerId, start);
    updateEntity(Episode, episode.data);
  };

  renderOverlay({ info, showSpinner }) {
    return (
      <div className="episode-editor-page__audio-upload-progress-overlay">
        <div className="episode-editor-page__audio-upload-progress-indication">
          <span>{showSpinner && <Loader width={8} />}</span>
          <span className="episode-editor-page__audio-upload-progress-indication-text">{info}</span>
        </div>
      </div>
    );
  }

  renderTimeline() {
    const { duration, silenceDetected } = this.props.mediaFile.data || {};
    const mediaUrl = this.props.mediaFile.data && this.props.mediaFile.data.getUrlOfEncodedFile();

    return (
      <Timeline
        markers={this.props.markers}
        selectedMarkerId={this.state.selectedMarkerId}
        episodeDuration={duration || undefined}
        silenceDetected={silenceDetected || undefined}
        mediaUrl={mediaUrl}
        zoomLevel={this.state.waveformZoomLevel}
        audioProgress={this.props.audioProgress}
        onSeek={this.props.seekAudio}
        onSelectMarker={this.handleSelectMarker}
        onUpdateMarker={this.handleUpdateMarker}
      />
    );
  }

  renderControlBar() {
    const { audioSource, isPlayingAudio, audioProgress, playAudio, pauseAudio } = this.props;
    const { waveformZoomLevel } = this.state;
    const isLoading = audioSource === EMPTY_AUDIO_SOURCE ? undefined : audioSource || undefined;

    return (
      <ControlBar
        isLoading={!isLoading}
        isPlaying={isPlayingAudio}
        audioProgress={audioProgress}
        waveformZoomLevel={waveformZoomLevel * 100}
        onPlay={playAudio}
        onPause={pauseAudio}
        onAddMarker={this.handleAddMarker}
        onWaveformZoom={this.handleZoom}
        onAudioFileUpload={this.handleAudioFileUpload}
      />
    );
  }

  renderPreviewPlayer() {
    const { audioProgress, markers, isPlayingAdJingle } = this.props;

    return (
      <PreviewPlayer
        audioProgress={audioProgress}
        markers={markers}
        isPlayingAdJingle={isPlayingAdJingle}
      />
    );
  }

  renderMarkerSettings() {
    const { markers, mediaFile } = this.props;
    const { selectedMarkerId } = this.state;
    const episodeDuration = mediaFile.data && mediaFile.data.duration;
    const silenceDetected = mediaFile.data && mediaFile.data.silenceDetected;
    const selectedMarker = markers.find(m => m.id === selectedMarkerId);

    if (!this.state.selectedMarkerId) {
      return <NoMarkerSelected />;
    }

    return (
      <MarkerSettings
        markers={markers}
        marker={selectedMarker}
        markerId={this.state.selectedMarkerId}
        episodeDuration={episodeDuration}
        onDeleteMarker={this.handleDeleteMarker}
        onUpdateMarker={this.handleUpdateMarker}
        silenceDetected={silenceDetected}
      />
    );
  }

  renderMarkerEditor() {
    const mediaFileData = this.props.mediaFile.data;
    const episodeDuration = mediaFileData && mediaFileData.duration;

    return (
      <>
        <div className="episode-editor-page__markers-list">
          <MarkersList
            markers={this.props.markers}
            episodeDuration={episodeDuration}
            selectedMarkerId={this.state.selectedMarkerId}
            onSelectMarker={this.handleSelectMarker}
          />
        </div>
        <div className="episode-editor-page__marker-settings">{this.renderMarkerSettings()}</div>
      </>
    );
  }

  render() {
    return (
      <div className="episode-editor-page">
        {this.maybeRenderOverlay()}
        <div className="episode-editor-page__audio-editor">
          {this.renderTimeline()}
          <div className="editor-controls-container">
            {this.renderControlBar()}
            {this.renderPreviewPlayer()}
          </div>
        </div>
        <div className="episode-editor-page__marker-editor">{this.renderMarkerEditor()}</div>
      </div>
    );
  }
}

const deriveAudioStateFromProps = (context, props) => {
  return {
    src: context.mediaFile.data && context.mediaFile.data.getUrlOfEncodedFile(),
    adMarkers: props.markers.filter(marker => marker.type === MARKER_TYPES.AD),
  };
};

const EditorViewContainer = (props, context) => {
  return (
    <AudioContainer {...deriveAudioStateFromProps(context, props)}>
      {({ src, paused, currentTime, play, pause, seek, isPlayingAdJingle }) => (
        <EditorView
          {...props}
          {...context}
          audioSource={src}
          isPlayingAudio={!paused}
          isPlayingAdJingle={isPlayingAdJingle}
          audioProgress={currentTime}
          playAudio={play}
          pauseAudio={pause}
          seekAudio={seek}
        />
      )}
    </AudioContainer>
  );
};

EditorViewContainer.contextTypes = {
  episode: resourceProp(Episode),
  mediaFile: resourceProp(MediaFile),
};

export default connect(
  (state, { params }) => ({
    markers: selectMarkers(state, params.episodeId),
  }),
  {
    onCreateMarker: createMarker,
    onDeleteMarker: deleteMarker,
    onUpdateMarker: updateMarker,
  }
)(EditorViewContainer);
